import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import {
  Plus, Target, PiggyBank, ShieldCheck, Home, GraduationCap, CreditCard,
  Car, Heart, Plane, Briefcase, Flag, TrendingUp, AlertTriangle, DollarSign,
} from 'lucide-react';
import CircularProgress from './CircularProgress';
import GoalDetail from './GoalDetail';
import { analyzeGoal, statusMeta, formatCurrency } from '../lib/goalMath';

export const CATEGORIES = [
  { value: 'retirement', label: 'Retirement', Icon: PiggyBank },
  { value: 'emergency_fund', label: 'Emergency Fund', Icon: ShieldCheck },
  { value: 'home', label: 'Home Purchase', Icon: Home },
  { value: 'education', label: 'Education', Icon: GraduationCap },
  { value: 'debt', label: 'Debt Reduction', Icon: CreditCard },
  { value: 'major_purchase', label: 'Major Purchase', Icon: Car },
  { value: 'wedding', label: 'Wedding', Icon: Heart },
  { value: 'travel', label: 'Travel', Icon: Plane },
  { value: 'business', label: 'Business', Icon: Briefcase },
  { value: 'custom', label: 'Custom Goal', Icon: Target },
];

export const categoryMeta = (value) => CATEGORIES.find((c) => c.value === value) || CATEGORIES[CATEGORIES.length - 1];

export const PRIORITIES = [
  { value: 'critical', label: 'Critical', badge: 'badge-danger' },
  { value: 'high', label: 'High', badge: 'badge-warning' },
  { value: 'medium', label: 'Medium', badge: 'badge-primary' },
  { value: 'low', label: 'Low', badge: 'badge-gray' },
];

const emptyForm = () => ({
  name: '',
  category: 'retirement',
  priority: 'high',
  target_amount: '',
  current_amount: '',
  target_date: '',
  expected_return: '6',
  monthly_contribution: '',
  linked_asset_ids: [],
  notes: '',
});

const Goals = ({ session, assets = [] }) => {
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingGoal, setEditingGoal] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [form, setForm] = useState(emptyForm());

  useEffect(() => {
    fetchGoals();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchGoals = async () => {
    try {
      const { data, error } = await supabase
        .from('goals')
        .select('*')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: true });
      if (error) throw error;
      setGoals(data || []);
    } catch (error) {
      console.error('Error fetching goals:', error);
    } finally {
      setLoading(false);
    }
  };

  // Live "current" value: sum of linked assets if any, otherwise the manual amount.
  const effectiveCurrent = (goal) => {
    const ids = goal.linked_asset_ids || [];
    if (ids.length > 0) {
      return assets
        .filter((a) => ids.includes(a.id))
        .reduce((s, a) => s + (parseFloat(a.value) || 0), 0);
    }
    return parseFloat(goal.current_amount) || 0;
  };

  const openCreate = () => {
    setEditingGoal(null);
    setForm(emptyForm());
    setShowForm(true);
  };

  const openEdit = (goal) => {
    setEditingGoal(goal);
    setForm({
      name: goal.name,
      category: goal.category || 'custom',
      priority: goal.priority || 'medium',
      target_amount: goal.target_amount?.toString() || '',
      current_amount: goal.current_amount?.toString() || '',
      target_date: goal.target_date || '',
      expected_return: (goal.expected_return ?? 6).toString(),
      monthly_contribution: goal.monthly_contribution?.toString() || '',
      linked_asset_ids: goal.linked_asset_ids || [],
      notes: goal.notes || '',
    });
    setDetailId(null);
    setShowForm(true);
  };

  const toggleAsset = (id) => {
    setForm((f) => ({
      ...f,
      linked_asset_ids: f.linked_asset_ids.includes(id)
        ? f.linked_asset_ids.filter((x) => x !== id)
        : [...f.linked_asset_ids, id],
    }));
  };

  const saveGoal = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      name: form.name,
      category: form.category,
      priority: form.priority,
      target_amount: parseFloat(form.target_amount) || 0,
      current_amount: form.linked_asset_ids.length > 0 ? 0 : parseFloat(form.current_amount) || 0,
      target_date: form.target_date || null,
      expected_return: parseFloat(form.expected_return) || 0,
      monthly_contribution: parseFloat(form.monthly_contribution) || 0,
      linked_asset_ids: form.linked_asset_ids,
      notes: form.notes || null,
    };
    try {
      if (editingGoal) {
        const { error } = await supabase.from('goals').update(payload).eq('id', editingGoal.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('goals').insert({ user_id: session.user.id, ...payload });
        if (error) throw error;
      }
      setShowForm(false);
      setEditingGoal(null);
      fetchGoals();
    } catch (error) {
      console.error('Error saving goal:', error);
      alert('Error saving goal: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteGoal = async (id) => {
    if (!window.confirm('Delete this goal?')) return;
    try {
      const { error } = await supabase.from('goals').delete().eq('id', id);
      if (error) throw error;
      setDetailId(null);
      fetchGoals();
    } catch (error) {
      console.error('Error deleting goal:', error);
      alert('Error deleting goal: ' + error.message);
    }
  };

  // Persist what-if changes coming from the detail screen.
  const applyChanges = async (id, updates) => {
    try {
      const { error } = await supabase.from('goals').update(updates).eq('id', id);
      if (error) throw error;
      fetchGoals();
    } catch (error) {
      console.error('Error updating goal:', error);
      alert('Error updating goal: ' + error.message);
    }
  };

  // ---- Overview metrics ----
  const analyses = goals.map((g) => ({ goal: g, a: analyzeGoal(g, effectiveCurrent(g)) }));
  const onTrack = analyses.filter((x) => x.a.status === 'on').length;
  const needAttention = goals.length - onTrack;
  const avgProgress = goals.length
    ? Math.round(analyses.reduce((s, x) => s + x.a.progressPct, 0) / goals.length)
    : 0;
  const totalFunding = analyses.reduce((s, x) => s + x.a.gap, 0);

  if (loading) return <div className="text-center py-12 text-gray-500">Loading your goals...</div>;

  const detailGoal = goals.find((g) => g.id === detailId);

  return (
    <div className="space-y-6">
      {/* Header row */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Financial Goals</h2>
          <p className="text-sm text-gray-500">Plan milestones and track your probability of success.</p>
        </div>
        <button onClick={openCreate} className="btn-primary btn-sm">
          <Plus className="h-4 w-4 mr-1.5" /> Add Goal
        </button>
      </div>

      {/* Overview cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Overview label="Active Goals" value={goals.length} Icon={Flag} tint="primary" />
        <Overview
          label="On Track"
          value={onTrack}
          sub={needAttention > 0 ? `${needAttention} need attention` : 'All on track'}
          Icon={needAttention > 0 ? AlertTriangle : TrendingUp}
          tint={needAttention > 0 ? 'warning' : 'success'}
        />
        <Overview label="Average Progress" value={`${avgProgress}%`} Icon={Target} tint="primary" />
        <Overview label="Future Funding Need" value={formatCurrency(totalFunding)} Icon={DollarSign} tint="danger" />
      </div>

      {/* Goal grid */}
      {goals.length === 0 ? (
        <div className="card p-12 text-center">
          <Target className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No goals yet. Add your first goal to start planning.</p>
          <button onClick={openCreate} className="btn-primary btn-sm mx-auto mt-4">
            <Plus className="h-4 w-4 mr-1.5" /> Add Goal
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {analyses.map(({ goal, a }) => {
            const { Icon, label } = categoryMeta(goal.category);
            const sm = statusMeta[a.status];
            const prio = PRIORITIES.find((p) => p.value === goal.priority) || PRIORITIES[2];
            return (
              <button
                key={goal.id}
                onClick={() => setDetailId(goal.id)}
                className="card p-5 text-left hover:shadow-md transition-shadow"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-10 w-10 rounded-lg bg-primary-50 flex items-center justify-center shrink-0">
                      <Icon className="h-5 w-5 text-primary-600" />
                    </div>
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 truncate">{goal.name}</p>
                      <p className="text-xs text-gray-500">{label}</p>
                    </div>
                  </div>
                  <span className={prio.badge}>{prio.label}</span>
                </div>

                <div className="flex items-center gap-4">
                  <CircularProgress
                    value={a.progressPct}
                    size={88}
                    stroke={9}
                    color={sm.ring}
                    label={`${Math.round(a.progressPct)}%`}
                  />
                  <div className="flex-1 space-y-1.5 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Current</span>
                      <span className="font-medium text-gray-900">{formatCurrency(a.current)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Target</span>
                      <span className="font-medium text-gray-900">{formatCurrency(a.target)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">By</span>
                      <span className="font-medium text-gray-900">
                        {goal.target_date ? new Date(goal.target_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : '—'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-3 border-t border-gray-100 flex items-center justify-between">
                  <span className={`badge ${sm.bg} ${sm.color}`}>{sm.label}</span>
                  <span className="text-xs text-gray-500">{a.probability}% success</span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Detail modal */}
      {detailGoal && (
        <GoalDetail
          goal={detailGoal}
          currentValue={effectiveCurrent(detailGoal)}
          assets={assets}
          onClose={() => setDetailId(null)}
          onEdit={() => openEdit(detailGoal)}
          onDelete={() => deleteGoal(detailGoal.id)}
          onApply={(updates) => applyChanges(detailGoal.id, updates)}
        />
      )}

      {/* Create / Edit modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full my-8">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">{editingGoal ? 'Edit Goal' : 'New Goal'}</h3>
            </div>
            <form onSubmit={saveGoal} className="px-6 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Goal name</label>
                <input
                  type="text" className="input" required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g., Retire at age 62"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select className="input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                    {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
                  <select className="input" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })}>
                    {PRIORITIES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Target amount ($)</label>
                  <input type="number" step="0.01" className="input" required
                    value={form.target_amount}
                    onChange={(e) => setForm({ ...form, target_amount: e.target.value })}
                    placeholder="2000000" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Target date</label>
                  <input type="date" className="input" required
                    value={form.target_date}
                    onChange={(e) => setForm({ ...form, target_date: e.target.value })} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Monthly savings ($)</label>
                  <input type="number" step="0.01" className="input"
                    value={form.monthly_contribution}
                    onChange={(e) => setForm({ ...form, monthly_contribution: e.target.value })}
                    placeholder="1175" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Expected return (%)</label>
                  <input type="number" step="0.1" className="input"
                    value={form.expected_return}
                    onChange={(e) => setForm({ ...form, expected_return: e.target.value })}
                    placeholder="6" />
                </div>
              </div>

              {/* Linked accounts */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Linked accounts <span className="text-gray-400 font-normal">— optional, current value updates automatically</span>
                </label>
                {assets.length === 0 ? (
                  <p className="text-xs text-gray-400">No assets yet. Add assets in the Net Worth tab to link them.</p>
                ) : (
                  <div className="max-h-36 overflow-y-auto border border-gray-200 rounded-md divide-y divide-gray-100">
                    {assets.map((a) => (
                      <label key={a.id} className="flex items-center justify-between px-3 py-2 cursor-pointer hover:bg-gray-50">
                        <span className="flex items-center gap-2 text-sm text-gray-700">
                          <input
                            type="checkbox"
                            className="h-4 w-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                            checked={form.linked_asset_ids.includes(a.id)}
                            onChange={() => toggleAsset(a.id)}
                          />
                          {a.name}
                        </span>
                        <span className="text-sm text-gray-500">{formatCurrency(a.value)}</span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Manual current amount only when nothing linked */}
              {form.linked_asset_ids.length === 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Current amount ($)</label>
                  <input type="number" step="0.01" className="input"
                    value={form.current_amount}
                    onChange={(e) => setForm({ ...form, current_amount: e.target.value })}
                    placeholder="735000" />
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes <span className="text-gray-400 font-normal">— optional</span></label>
                <textarea className="input min-h-[72px] py-2" rows={2}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="e.g., Want to retire before Medicare; need $8,000/month income." />
              </div>

              <div className="flex gap-3 pt-1">
                <button type="button" onClick={() => setShowForm(false)} className="btn-outline flex-1">Cancel</button>
                <button type="submit" disabled={saving} className="btn-primary flex-1">
                  {saving ? 'Saving...' : editingGoal ? 'Save Changes' : 'Create Goal'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const tintMap = {
  primary: 'bg-primary-50 text-primary-600',
  success: 'bg-success-50 text-success-600',
  warning: 'bg-warning-50 text-warning-600',
  danger: 'bg-danger-50 text-danger-600',
};

const Overview = ({ label, value, sub, Icon, tint }) => (
  <div className="card p-5 flex items-start justify-between">
    <div>
      <p className="text-sm font-medium text-gray-600">{label}</p>
      <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
    <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${tintMap[tint]}`}>
      <Icon className="h-5 w-5" />
    </div>
  </div>
);

export default Goals;
